import React, { useState, useCallback } from 'react';
import './Login.css';
import ApiClient from '../../api/apiClient';

const Login = ({ onLoginSuccess }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    organizationName: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(''); // Clear error when user types
  }, [error]);

  const validateForm = useCallback(() => {
    if (!isRegister) return null;
    
    if (!formData.displayName || !formData.email || !formData.organizationName || !formData.password || !formData.confirmPassword) {
      return 'All fields are required';
    }
    
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    
    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    
    return null;
  }, [isRegister, formData]);

  const handleEmailAuth = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    // Validate form first
    const formError = validateForm();
    if (isRegister && formError) {
      setError(formError);
      setLoading(false);
      return;
    }

    try {
      const endpoint = isRegister ? '/api/users/register' : '/api/users/login-email';
      const payload = isRegister 
        ? formData 
        : { email: formData.email, password: formData.password };

      const response = await ApiClient.post(endpoint, payload);

      if (response.data.success) {
        if (isRegister) {
          setSuccessMessage('Account created successfully!');
          setRegistrationSuccess(true);
          
          // Auto switch to login after success
          setTimeout(() => {
            setIsRegister(false);
            setRegistrationSuccess(false);
            setSuccessMessage('');
            setFormData(prev => ({ 
              ...prev, 
              displayName: '', 
              organizationName: '',
              password: '', 
              confirmPassword: '' 
            }));
          }, 2000);
        } else {
          if (response.data.token) {
            sessionStorage.setItem('authToken', response.data.token);
          }
          if (onLoginSuccess) {
            onLoginSuccess(response.data.user);
          }
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(error.response?.data?.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isRegister, formData, onLoginSuccess, validateForm]);

  const handleSignInTab = useCallback(() => {
    setIsRegister(false);
    setError('');
    setSuccessMessage('');
    setFormData(prev => ({
      ...prev,
      displayName: '',
      organizationName: '',
      confirmPassword: ''
    }));
  }, []);

  const handleSignUpTab = useCallback(() => {
    setIsRegister(true);
    setError('');
    setSuccessMessage('');
  }, []);

  const formError = validateForm();

  // Main authentication screen with all methods
  return (
    <div className="login-shell">
      <div className="login-bg">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />
      </div>
      <div className="login-panel">
        <div className="login-hero">
          <img src="/MBS-Monitoring-Logo.svg" alt="MBS Monitoring" className="hero-logo" />
          <div className="hero-text">
            <p className="hero-copy">Manage &amp; monitor your servers and APIs with ease.</p>
            <div className="hero-badges">
              <div className="hero-badge">
                <span className="hero-badge-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l7 4v5c0 4.2-2.7 7.9-7 9-4.3-1.1-7-4.8-7-9V7z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </span>
                <span>Secure Access</span>
              </div>
              <div className="hero-badge">
                <span className="hero-badge-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                </span>
                <span>Uptime Ready</span>
              </div>
              <div className="hero-badge">
                <span className="hero-badge-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 00-12 0v5H4a1 1 0 000 2h16a1 1 0 000-2h-2z" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                  </svg>
                </span>
                <span>Instant Alerts</span>
              </div>
            </div>
          </div>
          <div className="hero-links">
            <a
              className="hero-link"
              href="https://mbs-digitals.com/apps/mbs-monitoring"
              target="_blank"
              rel="noreferrer"
            >
              Documentation
            </a>
            <a
              className="hero-link"
              href="https://mbs-digitals.com/support"
              target="_blank"
              rel="noreferrer"
            >
              Support
            </a>
          </div>
        </div>

        <div className={`login-form-panel ${isRegister ? 'register-mode' : 'signin-mode'}`}>
          <div className="login-form-inner">
            <div className="login-header">
              <h2 className="login-title">{isRegister ? 'Create your account' : 'Sign in'}</h2>
              <p className="auth-subtitle">
                {isRegister ? 'Create your monitoring team.' : 'Sign in to your monitoring space.'}
              </p>
            </div>

            <div className="email-auth-tabs">
              <button 
                className={`tab-button ${!isRegister ? 'active' : ''}`}
                onClick={handleSignInTab}
                type="button"
              >
                Sign In
              </button>
              <button 
                className={`tab-button ${isRegister ? 'active' : ''}`}
                onClick={handleSignUpTab}
                type="button"
              >
                Create Account
              </button>
            </div>

            <div className="auth-mode-wrapper" key={isRegister ? 'register-mode' : 'signin-mode'}>
              {successMessage && (
                <div className="success-message" key="success-message">
                  <div className="success-icon">✓</div>
                  <div className="success-text">{successMessage}</div>
                </div>
              )}

              {error && <div className="error-message" key="error-message">{error}</div>}

              <form onSubmit={handleEmailAuth} className="compact-auth-form">
                <fieldset disabled={registrationSuccess} style={{ border: 'none', padding: 0, margin: 0 }}>
                  {isRegister && (
                    <div className="form-group animated-field" key="displayName-field">
                      <input
                        type="text"
                        id="displayName"
                        name="displayName"
                        value={formData.displayName}
                        onChange={handleInputChange}
                        placeholder="Full name"
                        required
                      />
                    </div>
                  )}
                  {isRegister && (
                    <div className="form-group animated-field" key="organizationName-field">
                      <input
                        type="text"
                        id="organizationName"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        placeholder="Organization name"
                        required
                      />
                    </div>
                  )}

                  <div className="form-group" key="email-field">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email address"
                      required
                    />
                  </div>

                  <div className="form-group" key="password-field">
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Password"
                      required
                    />
                  </div>

                  {isRegister && (
                    <div className="form-group animated-field" key="confirmPassword-field">
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm password"
                        required
                      />
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="auth-submit-button"
                    disabled={loading || registrationSuccess || (isRegister && formError)}
                  >
                    {loading ? 'Processing...' : (isRegister ? 'Create Account' : 'Sign In')}
                  </button>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
